import React, { useState } from 'react';
import { Acao, Cabecalho, Foto, OdsLista, Texto } from './comum';
import * as S from './style';

// O schema `destaque` é o mesmo para os destaques e as certificações; o
// desenho é escolhido aqui, pela seção e pela posição (é o que diz a dica do
// schema). Os três desenhos dos destaques se repetem se vierem mais de três.
const DESENHOS = ['colagem', 'rastreio', 'ciclo'];

// Os ícones dos indicadores acompanham a posição, como no protótipo: pessoas,
// fazenda, documento e selo. Indicador além do quarto vira número grande.
const tracos = { fill: 'none', stroke: 'var(--brand-600)', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };
const ICONES = [
  <svg width="20" height="20" viewBox="0 0 24 24" {...tracos} aria-hidden="true">
    <circle cx="9" cy="8" r="3" />
    <path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
    <circle cx="17" cy="9" r="2.6" />
    <path d="M15.5 14.2c2.7.4 4.5 2.4 4.5 5.3" />
  </svg>,
  <svg width="20" height="20" viewBox="0 0 24 24" {...tracos} aria-hidden="true">
    <path d="M4 21V10l8-6 8 6v11" />
    <path d="M9 21v-6h6v6" />
    <path d="M4 14h16" />
  </svg>,
  <svg width="20" height="20" viewBox="0 0 24 24" {...tracos} aria-hidden="true">
    <path d="M7 3h7l3 3v15H7z" />
    <path d="M14 3v3h3" />
    <path d="M10.5 14c1.5-2 3.5-2 3.5 0s-2 3.5-3.5 1.5" />
  </svg>,
  <svg width="20" height="20" viewBox="0 0 24 24" {...tracos} aria-hidden="true">
    <circle cx="12" cy="12" r="8" />
    <path d="M8.5 12.3 11 15l4.5-5.4" />
  </svg>,
];

const IconePdf = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 17h4" />
  </svg>
);

const inteiro = (n) => Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
const porcento = (n) => `${Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

const VolumePorCultura = ({ volumes, titulo, unidade }) => {
  if (!volumes?.length) return null;
  return (
    <S.Volumes>
      {titulo && <S.Rotulo>{titulo}</S.Rotulo>}
      <div>
        {volumes.map((v, i) => (
          <S.Volume key={i}>
            <div>
              <strong>{v.cultura}</strong>
              <span>
                {[inteiro(v.toneladas), unidade].filter(Boolean).join(' ')} · {porcento(v.percentual)}
              </span>
            </div>
            <div>
              <span style={{ width: `${Math.min(100, Math.max(0, Number(v.percentual) || 0))}%` }} />
            </div>
          </S.Volume>
        ))}
      </div>
    </S.Volumes>
  );
};

const Indicadores = ({ indicadores }) => {
  if (!indicadores?.length) return null;
  const linha = indicadores.slice(0, 4);
  const grandes = indicadores.slice(4);
  return (
    <>
      <S.Indicadores>
        {linha.map((n, i) => (
          <div key={i}>
            <span>{ICONES[i]}</span>
            <strong>{n.valor}</strong>
            <p>{n.rotulo}</p>
          </div>
        ))}
      </S.Indicadores>
      {grandes.map((n, i) => (
        <S.NumeroGrande key={i}>
          <strong>{n.valor}</strong>
          <span />
          <p>{n.rotulo}</p>
        </S.NumeroGrande>
      ))}
    </>
  );
};

const Resultado = ({ titulo, texto }) => {
  if (!titulo) return null;
  return (
    <S.Resultado>
      <p>{titulo}</p>
      {texto && <p>{texto}</p>}
    </S.Resultado>
  );
};

const fotosDe = (d) => (d.fotos || []).filter((f) => f?.foto?.[0]);

const Colagem = ({ fotos }) => (
  <S.Colagem>
    {fotos.slice(0, 4).map((f, i) => (
      <Foto key={i} id={f.foto[0]} largura={720} alt={f.legenda} title={f.legenda} />
    ))}
  </S.Colagem>
);

const Ciclo = ({ fotos, logo, titulo }) => (
  <S.Ciclo>
    <div>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <path
          d="M30,26 C58,22 66,42 70,56 C74,72 52,84 30,76 C10,68 12,38 30,26"
          fill="none"
          stroke="var(--brand-500)"
          strokeWidth="1.4"
          strokeDasharray="3 3"
          strokeLinecap="round"
        />
      </svg>
      {fotos.slice(0, 3).map((f, i) => (
        <Foto key={i} className="foto" id={f.foto[0]} largura={420} alt={f.legenda} title={f.legenda} />
      ))}
      {logo && (
        <span>
          <Foto id={logo} largura={300} alt={titulo} transparente />
        </span>
      )}
    </div>
  </S.Ciclo>
);

// O texto do "Destaque 02 · Rastreabilidade" vira o rótulo ao lado da folha.
const assuntoDoChapeu = (chapeu = '') => chapeu.split('·').pop().trim();

const Rastreio = ({ destaque, fotos }) => (
  <S.Rastreio>
    {fotos.length > 0 && (
      <S.Faixa $n={Math.min(fotos.length, 5)}>
        {fotos.slice(0, 5).map((f, i) => (
          <div key={i}>
            <Foto id={f.foto[0]} largura={400} alt={f.legenda} title={f.legenda} />
          </div>
        ))}
      </S.Faixa>
    )}

    {destaque.subtitulo && (
      <S.Folha>
        <svg width="46" height="46" viewBox="0 0 48 48" fill="none" stroke="var(--brand-600)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M39 9c0 12.5-7.8 22.5-19.5 22.5C13.2 31.5 9 27.3 9 21 9 12.7 22 9 39 9Z" />
          <path d="M9 40c3.5-11 12-19.5 24-26" />
          <path d="M20 20.5c1.6 2 3.6 3.6 6 4.6" />
          <path d="M27.5 14c1.4 1.8 3.1 3.2 5.2 4.2" />
        </svg>
        <span />
        <div>
          <p>{assuntoDoChapeu(destaque.chapeu) || destaque.titulo}</p>
          <p>{destaque.subtitulo}</p>
        </div>
      </S.Folha>
    )}

    <Indicadores indicadores={destaque.indicadores} />
  </S.Rastreio>
);

const Destaque = ({ destaque, desenho }) => {
  const fotos = fotosDe(destaque);
  const logo = destaque.logo?.[0];
  const rastreio = desenho === 'rastreio';

  let visual = null;
  if (rastreio) visual = <Rastreio destaque={destaque} fotos={fotos} />;
  else if (desenho === 'ciclo' && (fotos.length || logo)) visual = <Ciclo fotos={fotos} logo={logo} titulo={destaque.titulo} />;
  else if (fotos.length) visual = <Colagem fotos={fotos} />;

  const texto = (
    <S.DestaqueTexto>
      {destaque.chapeu && <S.Chapeu $cor="var(--accent)">{destaque.chapeu}</S.Chapeu>}
      <S.TituloComOds>
        <S.H3>{destaque.titulo}</S.H3>
        <OdsLista numeros={destaque.ods} tamanho={48} />
      </S.TituloComOds>
      {!rastreio && destaque.subtitulo && <S.Nota>{destaque.subtitulo}</S.Nota>}
      <Texto>{destaque.texto}</Texto>
      {!rastreio && <Indicadores indicadores={destaque.indicadores} />}
      <VolumePorCultura volumes={destaque.volumes} titulo={destaque.volumesTitulo} unidade={destaque.volumesUnidade} />
      <Resultado titulo={destaque.resultadoTitulo} texto={destaque.resultadoTexto} />
      <Acao acao={destaque.acao} icone={<IconePdf />} />
    </S.DestaqueTexto>
  );

  // Rastreio põe o texto à esquerda; os outros abrem pela imagem (o "Z").
  return (
    <S.Destaque $alinhar={desenho === 'ciclo' ? 'center' : 'flex-start'}>
      {rastreio ? texto : visual}
      {rastreio ? visual : texto}
    </S.Destaque>
  );
};

export const Destaques = ({ secao }) => (
  <S.Secao id="destaques">
    <S.Container>
      <Cabecalho chapeu={secao.chapeu} titulo={secao.titulo} descricao={secao.descricao} $cor="var(--accent)" />
      {(secao.destaques || []).map((d, i) => (
        <Destaque key={i} destaque={d} desenho={DESENHOS[i % DESENHOS.length]} />
      ))}
    </S.Container>
  </S.Secao>
);

// Logos de certificação vêm em proporções bem diferentes: o Better Cotton é
// alto, o RECFY é largo. Com a mesma largura, o largo ficava miúdo. Aqui cada
// logo recebe a mesma ÁREA, então todos têm o mesmo peso visual; a largura sai
// da proporção real do arquivo, lida quando a imagem carrega. O teto de altura
// e de largura fica no CSS (max-height/max-width do .logo).
const LogoCertificacao = ({ id, alt, area }) => {
  const [largura, setLargura] = useState(null);
  return (
    <Foto
      className="logo"
      id={id}
      largura={640}
      alt={alt}
      transparente
      onLoad={(e) => {
        const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
        if (w && h) setLargura(Math.round(Math.sqrt(area * (w / h))));
      }}
      style={{ width: largura || Math.sqrt(area) }}
    />
  );
};

// Área do logo sozinho no quadro, e ao lado dos selos (divide o espaço).
const AREA_LOGO = 220 * 300;
const AREA_LOGO_COM_SELOS = 150 * 150;

const Certificacao = ({ item, invertido, rotuloOds, rotuloFotos }) => {
  const selos = fotosDe(item);
  const logo = item.logo?.[0];

  return (
    <S.Certificacao>
      <S.CertCartao $inv={invertido}>
        <div>
          {item.chapeu && <S.Chapeu $cor="var(--accent)">{item.chapeu}</S.Chapeu>}
          <S.H3>{item.titulo}</S.H3>
          {item.subtitulo && <S.Nota as="p" style={{ fontSize: 'var(--fs-body)' }}>{item.subtitulo}</S.Nota>}
          <Texto>{item.texto}</Texto>
          {item.ods?.length > 0 && (
            <S.Contribui>
              {rotuloOds && <S.Rotulo as="span">{rotuloOds}</S.Rotulo>}
              <OdsLista numeros={item.ods} tamanho={52} />
            </S.Contribui>
          )}
        </div>

        {(logo || selos.length > 0) && (
          <S.CertVisual>
            {selos.length > 0 && (
              <>
                {rotuloFotos && <S.Rotulo>{rotuloFotos}</S.Rotulo>}
                <S.Marcos>
                  {selos.map((s, i) => (
                    <figure key={i}>
                      <Foto id={s.foto[0]} largura={200} alt={s.legenda} transparente />
                      <figcaption>{s.legenda}</figcaption>
                    </figure>
                  ))}
                </S.Marcos>
              </>
            )}
            {logo && (
              <LogoCertificacao
                id={logo}
                alt={item.titulo}
                area={selos.length ? AREA_LOGO_COM_SELOS : AREA_LOGO}
              />
            )}
          </S.CertVisual>
        )}
      </S.CertCartao>
    </S.Certificacao>
  );
};

export const Certificacoes = ({ secao }) => (
  <S.Secao id="certificacoes" $wash>
    <S.Container>
      <S.Bloco>
        <Cabecalho chapeu={secao.chapeu} titulo={secao.titulo} descricao={secao.descricao} />
      </S.Bloco>
      <S.Trilha>
        {(secao.destaques || []).map((d, i) => (
          <Certificacao
            key={i}
            item={d}
            invertido={i % 2 === 1}
            rotuloOds={secao.rotuloOds}
            rotuloFotos={secao.rotuloFotos}
          />
        ))}
      </S.Trilha>
    </S.Container>
  </S.Secao>
);
